// 계약 사실화 1단계 정정5(26-07-29) — ContractLine.reason(판매/렌탈/파일럿 …) 규칙의 **정본**.
//
// 여기 두는 규칙은 두 가지다:
//   ① deriveContractReason  — 로봇 한 대의 사유를 계약 줄에서 파생(모델 일치 → 전체 폴백
//                             → 갈리면 판정 불가).
//   ② contractTypeToLineReason — 계약 형태 한 단어('판매'/'렌탈' · 'SALE'/'RENTAL')를
//                             줄 사유로 옮긴다.
//
// ① 을 왜 정본으로 올렸는가(정정5의 이유):
// 종전에는 같은 규칙이 **두 벌** 있었다 —
//   • apps/api robots.service.buildSummary 안의 인라인 블록: 로봇 목록 '구분' 칸과
//     로봇 배치 모달의 '자동 판정' 미리보기가 이 값을 읽는다(화면이 보여 주는 값).
//   • apps/api deployments/cascade-deploy.helper.deriveDeploymentReasonFromLines:
//     실제 저장되는 Deployment.reason 이 이 값이다(인수확인서에 인쇄되는 값).
// 두 구현은 동작이 같았지만 **우연히** 같았다. 앞 라운드가 "화면이 미리 보여준 값과
// 저장값이 같다"를 안전장치로 삼았는데, 그 안전장치가 두 벌의 우연한 일치에 의존하고
// 있었다 — 한쪽만 고치면 미리보기와 계약서가 조용히 갈린다(운영자는 '판매'로 배치했는데
// 인수확인서에는 구독이 인쇄되는 사고). 그래서 순수 함수 하나로 합쳤다.
//
// 이 파일에 두는 이유: 파생 규칙(spec-match / contract-pricing / robot-status / calendar)
// 은 이 패키지에 모아 두는 것이 이 리포의 관례이고, 규칙이 다루는 DeploymentReason enum
// 자체도 여기(enums.ts) 있다. 조회는 호출부의 일이므로 이 함수들은 **DB 를 모른다** —
// 줄 배열과 모델 id 만 받는 순수 함수다(한쪽 호출부는 select 결과를, 다른 쪽은 트랜잭션
// 조회 결과를 넘긴다).
import { DeploymentReason } from './enums.js';
/**
 * 계약 사실화 1단계 정정5(26-07-29) — 로봇 한 대의 계약 사유를 계약 줄에서 파생한다(정본).
 *
 *   ① 로봇 모델과 일치하는 줄이 있으면 **그 줄들만** 후보로 본다. 배정함(assignments)도
 *      로봇을 같은 모델 줄에만 귀속시키므로(모델 불일치 줄로의 폴백 없음) 두 화면의 판정이
 *      어긋나지 않는다.
 *   ② 모델을 모르거나(호출부가 modelId 를 넘기지 않음) 일치 줄이 없으면(모델을 하드 필터
 *      하지 않은 백필로 붙은 로봇) 계약 **전체 줄**을 후보로 본다 — 전 줄이 한 가지 사유면
 *      어느 줄에 귀속되든 답이 같아 안전하다.
 *   ③ 후보 줄들의 사유가 서로 다르면 **추측하지 않는다** — reason=null + ambiguous=true.
 *      조용히 틀린 값보다 '확인 필요'가 낫다는 것이 이 정정의 원칙이다.
 *
 * 사유가 빈 줄은 후보에서 제외한다(신호 없음). DB 컬럼은 non-null 이지만 호출부가 부분
 * select 나 옛 payload 를 넘길 수 있어 방어적으로 둔다.
 */
export function deriveContractReason(lines, modelId) {
    const withReason = lines.filter((l) => !!l.reason);
    if (withReason.length === 0)
        return { reason: null, ambiguous: false };
    const modelLines = modelId ? withReason.filter((l) => l.modelId === modelId) : [];
    const candidates = modelLines.length > 0 ? modelLines : withReason;
    const reasons = new Set(candidates.map((l) => l.reason));
    if (reasons.size === 1)
        return { reason: candidates[0].reason, ambiguous: false };
    return { reason: null, ambiguous: true };
}
/**
 * 계약 사실화 1단계 정정5(26-07-29) — 계약 형태 한 단어를 줄 사유로 옮긴다(정본).
 *
 * **한 필드에 두 어휘가 실제로 섞여 저장돼 있다** — Deal.expectedContractType 을 쓰는
 * 화면이 둘이고 서로 다른 문자열을 보낸다:
 *   • 딜 등록 모달(pages/deals/DealCreateModal) — 화면에는 '판매'/'렌탈'을 보여 주지만
 *     서버로 보내는 값은 **영문** 'SALE' / 'RENTAL' 이다(option value). 스키마 주석과
 *     프론트 API 타입 주석도 "RENTAL | SALE" 이라고 적고 있다.
 *   • 고객사 마스터 모달 → customer-master.service 가 만드는 Deal — 그 화면의 공통
 *     '계약 형태' 칸 값을 그대로 넣으므로 **한글** '판매' / '렌탈' 이다.
 * 그래서 한쪽 어휘만 보는 판정은 다른 쪽 경로에서 항상 빗나간다(아래 정정 근거).
 *
 * 인식하지 못하는 값(미입력 · 옵션 관리자가 새로 만든 단어 등)은 **null**(신호 없음)로
 * 돌려준다 — 호출부가 종전 기본값을 유지하도록. 여기서 임의로 한쪽으로 접으면
 * "계약 형태를 안 골랐는데 구독이 된다"는 옛 사고가 되돌아온다.
 *
 * 영문은 대소문자·앞뒤 공백을 무시한다(옵션 그룹 값은 관리자가 직접 입력하는 자유
 * 문자열이다). 한글은 두 단어가 정확히 이 형태로만 저장된다.
 *
 * 계약 사실화 1단계 정정6(26-07-29) — **이제 두 저장 경로가 모두 이 함수를 부른다**:
 * 계약 합성(apps/api contracts.service.createSignedFromDeal)과 고객사 마스터
 * (apps/api customer-master.service — resolveLineReason · syncContractLines).
 * 정정5 는 여기 "정본"이라 적었지만 고객사 마스터는 계속 자기 사본(한글 2단어 정확
 * 일치)을 썼고, 그래서 트래픽이 가장 많은 그 경로로 들어온 'SALE' / ' 판매 ' 가
 * 조용히 기본값(구독)으로 떨어졌다. 사본은 삭제했다.
 *
 * ⚠ 이 함수를 고쳐 **받는 어휘를 넓히면** 고객사 마스터의 '접힌 메아리' 판정
 * (customer-master.service isFoldedEcho — "들어온 표기가 기존 값을 접은 결과와 같으면
 * 바꾸라는 뜻이 아니다")도 같이 넓어져야 한다. 그 판정은 새 어휘를 **이 함수로 환산해서**
 * 비교하므로 지금은 자동으로 따라온다 — 글자 비교로 되돌리지 말 것. 되돌리면 파일럿·
 * 시연·행사·사내·금융리스 줄이 'RENTAL' 한 번에 구독으로 영구 변경된다.
 */
export function contractTypeToLineReason(contractType) {
    if (!contractType)
        return null;
    const raw = contractType.trim();
    if (raw === '판매')
        return DeploymentReason.SALE;
    if (raw === '렌탈')
        return DeploymentReason.LEASE_OPERATING;
    const upper = raw.toUpperCase();
    if (upper === 'SALE')
        return DeploymentReason.SALE;
    if (upper === 'RENTAL')
        return DeploymentReason.LEASE_OPERATING;
    return null;
}
/**
 * 다계약 표시 차수3(26-08-05) — 이 계약이 **판매 전용**인가(모든 줄이 SALE).
 *
 * 왜 이 파일이 정본인가 — 같은 질문을 하는 자리가 둘이고 답이 갈리면 안 된다:
 *   • 재계약율 집계(apps/api dashboard/renewal-rate.service) — 판매 계약은 **갱신이라는
 *     사건이 없어** 모수에서 뺀다.
 *   • 고객사 마스터 시트(apps/api customer-master.row) — 판매는 소유가 고객사로 넘어가
 *     **반납이라는 사건이 없다.** 그래서 반납일·D-Day·계약기간·도입일이 볼 기준 계약에서 뺀다.
 * 종전엔 앞쪽에만 사본이 있었다(모듈 private). 뒤쪽이 리터럴 비교를 새로 적으면 "재계약율은
 * 빼는데 시트는 세는" 어긋남이 열리고, 그 어긋남은 두 화면을 나란히 놓고 봐야 보인다.
 *
 * 줄이 **0개**인 계약은 판매 전용이 **아니다**(false). '판매 전용'은 줄에 대한 주장이고,
 * 줄이 없으면 그 주장을 할 근거가 없다 — 모르는 것을 지어내지 않는다. 실무에서 줄 0개는
 * '계약 조건만 먼저 입력한 계약'(체결 직후)이라 반납일도 갱신도 계속 지켜봐야 한다.
 */
export function isSaleOnlyContract(lines) {
    return lines.length > 0 && lines.every((l) => l.reason === DeploymentReason.SALE);
}
//# sourceMappingURL=contract-line-reason.js.map